import { Input } from "../ui/input";
import { Label } from "../ui/label";

export const EXTRA_CHARGE_FIELDS = [
  { key: "halting_charge", label: "Halting Charge", testId: "invoice-halting-charge-input" },
  { key: "loading_unloading_charge", label: "Loading/Unloading Charge", testId: "invoice-loading-charge-input" },
  { key: "toll_charge", label: "Toll Charge", testId: "invoice-toll-charge-input" },
];

export const getTaxableAmount = (data) =>
  (data.subtotal || 0) + EXTRA_CHARGE_FIELDS.reduce((sum, f) => sum + (parseFloat(data[f.key]) || 0), 0);

// Recompute GST amounts and total from subtotal + extra charges + percentages
export const recalcInvoice = (data) => {
  const taxable = getTaxableAmount(data);
  let sgst_amount = 0, cgst_amount = 0, igst_amount = 0;
  if (data.gst_type === "sgst_cgst") {
    sgst_amount = taxable * ((data.sgst_percentage || 0) / 100);
    cgst_amount = taxable * ((data.cgst_percentage || 0) / 100);
  } else {
    igst_amount = taxable * ((data.igst_percentage || 0) / 100);
  }
  const gst_amount = sgst_amount + cgst_amount + igst_amount;
  return { ...data, sgst_amount, cgst_amount, igst_amount, gst_amount, total_amount: taxable + gst_amount };
};

export const InvoiceChargesSection = ({ formData, onChange, formatCurrency }) => {
  const update = (patch) => onChange(recalcInvoice({ ...formData, ...patch }));

  const handleGstType = (gst_type) =>
    update(gst_type === "sgst_cgst"
      ? { gst_type, sgst_percentage: 9, cgst_percentage: 9, igst_percentage: 0, gst_percentage: 18 }
      : { gst_type, sgst_percentage: 0, cgst_percentage: 0, igst_percentage: 18, gst_percentage: 18 });

  const handleTotalGst = (value) => {
    const total = parseFloat(value) || 0;
    update({ gst_percentage: total, sgst_percentage: total / 2, cgst_percentage: total / 2 });
  };

  const handlePct = (field, value) => {
    const v = parseFloat(value) || 0;
    const patch = { [field]: v };
    if (field === "igst_percentage") patch.gst_percentage = v;
    else patch.gst_percentage = (field === "sgst_percentage" ? v : formData.sgst_percentage) + (field === "cgst_percentage" ? v : formData.cgst_percentage);
    update(patch);
  };

  const pctInput = (field, placeholder, testId) => (
    <Input type="number" step="0.01" value={formData[field] || ""} onChange={(e) => handlePct(field, e.target.value)}
      className="bg-secondary/50 border-border h-9" placeholder={placeholder} data-testid={testId} />
  );

  return (
    <div className="space-y-4 p-4 bg-secondary/30 rounded-lg" data-testid="invoice-charges-section">
      <div className="flex justify-between text-sm">
        <span className="text-muted-foreground">Subtotal (Freight):</span>
        <span className="font-mono text-foreground" data-testid="invoice-subtotal">{formatCurrency(formData.subtotal)}</span>
      </div>

      <div className="space-y-2">
        <Label className="text-primary font-semibold">Extra Charges (Taxable)</Label>
        {EXTRA_CHARGE_FIELDS.map((f) => (
          <div key={f.key} className="flex items-center justify-between gap-4 text-sm">
            <Label htmlFor={f.key} className="text-muted-foreground">{f.label} (₹):</Label>
            <Input id={f.key} type="number" step="0.01" min="0" value={formData[f.key] || ""}
              onChange={(e) => update({ [f.key]: parseFloat(e.target.value) || 0 })}
              className="bg-secondary/50 border-border h-9 w-40 text-right font-mono" placeholder="0" data-testid={f.testId} />
          </div>
        ))}
      </div>
      <div className="flex justify-between text-sm font-semibold border-t border-border/50 pt-2">
        <span className="text-muted-foreground">Taxable Amount:</span>
        <span className="font-mono text-foreground" data-testid="invoice-taxable-amount">{formatCurrency(getTaxableAmount(formData))}</span>
      </div>

      <div className="border-t border-border pt-3 space-y-3">
        <Label className="text-primary font-semibold">GST Configuration</Label>
        <div className="flex gap-4">
          {[["sgst_cgst", "SGST + CGST (Intra-State)"], ["igst", "IGST (Inter-State)"]].map(([val, label]) => (
            <label key={val} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="gst_type" value={val} checked={formData.gst_type === val}
                onChange={() => handleGstType(val)} className="text-primary" data-testid={`invoice-gst-type-${val}`} />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>

        {formData.gst_type === "sgst_cgst" ? (
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Total GST %</Label>
              <Input type="number" step="0.01" value={formData.gst_percentage || ""} onChange={(e) => handleTotalGst(e.target.value)}
                className="bg-secondary/50 border-border h-9" placeholder="18" data-testid="invoice-total-gst-input" />
              <span className="text-xs text-muted-foreground">Auto-splits to SGST & CGST</span>
            </div>
            <div><Label className="text-xs text-muted-foreground">SGST %</Label>{pctInput("sgst_percentage", "9", "invoice-sgst-input")}</div>
            <div><Label className="text-xs text-muted-foreground">CGST %</Label>{pctInput("cgst_percentage", "9", "invoice-cgst-input")}</div>
          </div>
        ) : (
          <div className="w-1/3"><Label className="text-xs text-muted-foreground">IGST %</Label>{pctInput("igst_percentage", "18", "invoice-igst-input")}</div>
        )}

        <div className="bg-blue-950/30 rounded-lg p-3 space-y-1">
          {formData.gst_type === "sgst_cgst" ? (
            <>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">SGST ({formData.sgst_percentage}%):</span><span className="font-mono text-foreground">{formatCurrency(formData.sgst_amount)}</span></div>
              <div className="flex justify-between text-sm"><span className="text-muted-foreground">CGST ({formData.cgst_percentage}%):</span><span className="font-mono text-foreground">{formatCurrency(formData.cgst_amount)}</span></div>
            </>
          ) : (
            <div className="flex justify-between text-sm"><span className="text-muted-foreground">IGST ({formData.igst_percentage}%):</span><span className="font-mono text-foreground">{formatCurrency(formData.igst_amount)}</span></div>
          )}
          <div className="flex justify-between text-sm font-semibold border-t border-border/50 pt-1 mt-1">
            <span className="text-muted-foreground">Total GST:</span>
            <span className="font-mono text-foreground" data-testid="invoice-gst-amount">{formatCurrency(formData.gst_amount)}</span>
          </div>
        </div>
      </div>

      <div className="flex justify-between text-lg font-bold border-t border-border pt-2">
        <span className="text-foreground">Total Amount:</span>
        <span className="font-mono text-primary" data-testid="invoice-total-amount">{formatCurrency(formData.total_amount)}</span>
      </div>
    </div>
  );
};
